// `territories`: Swift `Territory` (GroundTeam/Models/Territory.swift). Unlike records, `updatedAt` is a synced key here.
// `polygon` is an open ring (the first point is not repeated at the end).
//
// Decoding template `Territory(name: "", polygon: [Coordinate(latitude: 0, longitude: 0)])`
// (GroundTeam/Sync/ResilientDecoding.swift:113). ResilientDecoding.merge lays every stored polygon point over that
// template point, so a point missing `latitude` or `longitude` reads 0 there, and a payload with no `polygon` key at
// all reads as the one-point polygon [(0, 0)]. Both are mirrored so the console shows what an iPhone shows. A new
// territory's polygon defaults to [] (the property default), as on iOS.

import { TERRITORY_KINDS, type TerritoryKind } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import { TEMPLATED_COORDINATE, newCoordinate, type CoordinatePayload } from './common';
import {
  BOOL,
  DATE,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface TerritoryPayload extends Passthrough {
  id: string;
  name: string;
  kind: TerritoryKind;
  colorHex: string;
  polygon: CoordinatePayload[];
  postalCodes: string[];
  assignedUserIds: string[];
  teamId?: string;
  notes: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const TERRITORY_KEYS = [
  'id',
  'name',
  'kind',
  'colorHex',
  'polygon',
  'postalCodes',
  'assignedUserIds',
  'teamId',
  'notes',
  'isActive',
  'createdAt',
  'updatedAt',
] as const satisfies readonly (keyof Known<TerritoryPayload>)[];

export const DEFAULT_TERRITORY_COLOR = '#2673E6';

export const TERRITORY_FIELDS: FieldMap<TerritoryPayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  kind: field.req(enumOf('TerritoryKind', TERRITORY_KINDS), () => 'custom'),
  colorHex: field.req(STRING, () => DEFAULT_TERRITORY_COLOR),
  polygon: field.req(arrayOf(TEMPLATED_COORDINATE), () => [{ latitude: 0, longitude: 0 }]),
  postalCodes: field.req(arrayOf(STRING), () => []),
  assignedUserIds: field.req(arrayOf(UUID), () => []),
  teamId: field.opt(UUID),
  notes: field.req(STRING, () => ''),
  isActive: field.req(BOOL, () => true),
  createdAt: field.req(DATE, nowWire),
  updatedAt: field.req(DATE, nowWire),
};

export function decodeTerritory(raw: JsonObject, envelopeId?: string): Decoded<TerritoryPayload> {
  return decodeModel(TERRITORY_FIELDS, raw, {}, envelopeId);
}

export type TerritoryInit = Partial<Known<TerritoryPayload>> & { name: string };

/** A new territory; `createdAt` and `updatedAt` share one timestamp, as Swift's `.now` defaults do in practice. */
export function newTerritory(init: TerritoryInit): TerritoryPayload {
  const now = nowWire();
  const { polygon, ...rest } = init;
  const defaults = {
    id: newId(),
    kind: 'custom',
    colorHex: DEFAULT_TERRITORY_COLOR,
    polygon: (polygon ?? []).map((p) => newCoordinate(p.latitude, p.longitude)),
    postalCodes: [],
    assignedUserIds: [],
    notes: '',
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
  return createModel(TERRITORY_FIELDS, defaults, rest, 'territories');
}
